import { BaseDetails } from "../BaseDetails";
import { Button } from "components/buttons/Button";
import { ContextMenu } from "components/inputs/context-menu";
import { DetailRow } from "../BaseDetails/DetailRow";
import PencilIcon from "assets/icons/pencil-simple.svg?react";
import styles from "./index.module.scss";
import { useIsMobile } from "hooks/useIsMobile";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { ComponentMap } from "components/utility/ComponentMap";
import { AssetResponse } from "@switcherstudio/api-core-client";
import { AssetThumbnail } from "components/thumbnails/AssetThumbnail";
import TrashIcon from "assets/icons/trash-sm.svg?react";
import { ProcessingThumbnail } from "components/thumbnails/ProcessingThumbnail";

export interface AssetDetailsProps {
    asset: AssetResponse;
    handleAssetDeleted: () => void;
    handleAssetEdited: () => void;
}

export const AssetDetails: React.FC<AssetDetailsProps> = ({
    asset,
    handleAssetDeleted,
    handleAssetEdited
}) => {
    const { isMobile } = useIsMobile();
    const disabled = useMemo(() => !asset?.Details?.Id, [asset]);
    const { t } = useTranslation();

    const mobileButtonsMenuItems = useMemo(() => {
        if (disabled) {
            return [
                {
                    key: "Delete",
                    text: t("buttons:delete"),
                    onClick: () => {
                        handleAssetDeleted();
                    }
                }
            ];
        }
        return [
            {
                key: "Edit",
                text: t("buttons:edit"),
                onClick: () => {
                    handleAssetEdited();
                }
            },
            {
                key: "Delete",
                text: t("buttons:delete"),
                onClick: () => {
                    handleAssetDeleted();
                }
            }
        ];
    }, [disabled, t, handleAssetDeleted, handleAssetEdited]);

    // raw files use thumbnail url, normal assets use url
    const imageUrl = useMemo(() => {
        switch (asset?.Details?.Type) {
            case "Art":
                return asset?.Details?.ThumbnailUrl;
            default:
                return asset?.Details?.Url;
        }
    }, [asset.Details]);

    const assetProcessingOrProvisioned = useMemo(
        () =>
            asset?.Details?.Status === "Processing" ||
            asset?.Details?.Status === "Provisioned" ? (
                <p>{t("cloud-assets:processing")}</p>
            ) : null,
        [asset, t]
    );

    const thumbnail = useMemo(() => {
        return assetProcessingOrProvisioned ? (
            <ProcessingThumbnail copy={t("cloud-assets:asset-processing")} />
        ) : (
            <AssetThumbnail Url={imageUrl} />
        );
    }, [assetProcessingOrProvisioned, imageUrl, t]);

    return (
        <BaseDetails
            isDraggable={false}
            actionItems={
                !isMobile ? (
                    <>
                        <Button
                            type="icon"
                            onClick={handleAssetEdited}
                            disabled={disabled}
                            title={t("buttons:edit")}
                        >
                            <PencilIcon />
                        </Button>
                        <Button
                            type="icon"
                            onClick={handleAssetDeleted}
                            disabled={disabled}
                            title={t("buttons:delete")}
                        >
                            <TrashIcon />
                        </Button>
                    </>
                ) : (
                    <ContextMenu
                        dropDirection="up"
                        items={mobileButtonsMenuItems}
                    />
                )
            }
            detailRows={
                <>
                    {disabled ? (
                        <p className={styles["disabled"]}>
                            {t("cloud-assets:disabled")}
                        </p>
                    ) : (
                        <DetailRow variant="info-row">
                            <ComponentMap
                                items={[assetProcessingOrProvisioned]}
                                element={(i) => i}
                                separator={<span>{"•"}</span>}
                            />
                        </DetailRow>
                    )}
                </>
            }
            thumbnail={thumbnail}
            title={asset?.Details?.Name}
        />
    );
};
